/**
 * Observation proxy for the PostgREST query builder.
 *
 * Wraps one builder chain so the request it sends is reported to the query
 * observer with its table, operation, and filter columns. The wrapper is
 * transparent: chained methods still hit the underlying SDK builder, results
 * are untouched, and observation failures — at issue time or at settle time —
 * can never fail a query.
 *
 * Only shapes are captured. `or()` and `range()` arguments are deliberately not
 * captured because they carry values rather than column names.
 */

import type { QueryCompletion, QueryObserver } from "@/lib/db/query-observer";
import type { ObservedQuery, QueryOperation } from "@/lib/db/query-shape";
import type { QueryBuilder } from "./types";

/**
 * Builder methods whose first argument is a column name.
 *
 * `or()` is excluded on purpose: its argument is a filter expression carrying
 * values (`"id.eq.42,uid.eq.INV-7"`), so capturing it would leak row data.
 */
const FILTER_METHODS = new Set([
  "contains",
  "eq",
  "filter",
  "gt",
  "gte",
  "ilike",
  "in",
  "is",
  "like",
  "lt",
  "lte",
  "neq",
  "not",
  "textSearch",
]);

/** Builder methods that switch the request away from a read. */
const MUTATION_OPERATIONS: Record<string, QueryOperation> = {
  delete: "delete",
  insert: "insert",
  update: "update",
};

/**
 * Filter methods that match a whole set in one round trip.
 *
 * These are the batching tools the detector recommends, so a read using one
 * must not share a signature with a per-row `.eq()`: `.in("id", ids)` reads a
 * hundred rows in one request, and reporting it as an N+1 is the false alarm
 * that gets the detector switched off.
 */
const BATCHING_METHODS = new Set(["in", "or"]);

/** Builder methods that page a read, distinguishing pages from repeats. */
const PAGINATION_METHODS = new Set(["range"]);

/** The builder method that sends the request. */
const SEND_METHOD = "then";

/** Builder methods that may return an already-sent request. */
const SINGLE_ROW_METHODS = new Set(["maybeSingle", "single"]);

/** Shape collected while a builder chain is assembled. */
export interface QueryState {
  batched: boolean;
  readonly filterColumns: string[];
  operation: QueryOperation;
  paginated: boolean;
  readonly table: string;
}

/**
 * Copies the shape collected so far.
 *
 * PostgREST hands back a *new* builder from `select()`/`insert()`/`update()`/
 * `delete()`, and one table builder can spawn several. Each new builder is an
 * independent request, so it gets its own state: sharing one object let a
 * filter added to one in-flight chain rewrite the signature recorded for
 * another.
 */
function cloneQueryState(state: QueryState): QueryState {
  return {
    batched: state.batched,
    filterColumns: [...state.filterColumns],
    operation: state.operation,
    paginated: state.paginated,
    table: state.table,
  };
}

function isPromiseLike(value: unknown): value is PromiseLike<unknown> {
  return (
    typeof value === "object" &&
    value !== null &&
    typeof (value as PromiseLike<unknown>).then === "function"
  );
}

/**
 * Whether a returned value continues the query chain.
 *
 * PostgREST hands back a *new* builder from `select()`, `insert()`, `update()`,
 * and `delete()`, and those builders are themselves thenable — so presence of
 * `then` cannot separate a builder from a promise. Chain methods identify a
 * builder instead.
 */
function isQueryBuilderLike(value: unknown): boolean {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const candidate = value as Record<string, unknown>;
  return typeof candidate.select === "function" || typeof candidate.eq === "function";
}

const NO_OP_COMPLETION: QueryCompletion = () => {
  // No observation to complete.
};

/**
 * Observation is diagnostics: a failing observer must not fail the query, so
 * both phases are guarded and the returned completion is safe to call from a
 * promise callback where a throw would surface as an unhandled rejection.
 */
export function recordSafely(observer: QueryObserver, query: ObservedQuery): QueryCompletion {
  let complete: QueryCompletion;
  try {
    complete = observer.record(query);
  } catch {
    return NO_OP_COMPLETION;
  }
  return (durationMs) => {
    try {
      complete(durationMs);
    } catch {
      // Diagnostics only — swallow so data access behavior is unchanged.
    }
  };
}

/** Applies a chained call's shape to the query state. */
function captureCall(state: QueryState, name: string, args: unknown[]): void {
  if (FILTER_METHODS.has(name)) {
    captureFilterColumn(state, args[0]);
  }
  if (BATCHING_METHODS.has(name)) {
    state.batched = true;
  }
  if (PAGINATION_METHODS.has(name)) {
    state.paginated = true;
  }
  const mutation = MUTATION_OPERATIONS[name];
  if (mutation) {
    state.operation = mutation;
  }
}

function captureFilterColumn(state: QueryState, column: unknown): void {
  if (typeof column === "string") {
    state.filterColumns.push(column);
  }
}

/** Whether the call may hand back an already-sent request. */
function maySendRequest(name: string): boolean {
  if (name === SEND_METHOD) {
    return true;
  }
  return SINGLE_ROW_METHODS.has(name);
}

/**
 * Records the request at issue time — analysis must not depend on when the
 * response arrives — then times the round trip without touching the caller's
 * promise chain.
 */
function recordSend(context: ObservationContext, result: unknown, startedAt: number): void {
  const { clock, observer, state } = context;
  const complete = recordSafely(observer, {
    batched: state.batched,
    filterColumns: [...state.filterColumns],
    operation: state.operation,
    paginated: state.paginated,
    startedAt,
    table: state.table,
  });
  if (!isPromiseLike(result)) {
    complete(clock() - startedAt);
    return;
  }
  const settle = () => {
    complete(clock() - startedAt);
  };
  result.then(settle, settle);
}

interface ObservationContext {
  readonly clock: () => number;
  readonly observer: QueryObserver;
  readonly state: QueryState;
}

/** One intercepted builder call and what the SDK returned for it. */
interface ChainedCall<T> {
  readonly args: unknown[];
  readonly name: string;
  readonly proxy: QueryBuilder<T>;
  readonly result: unknown;
  readonly startedAt: number;
  readonly target: object;
}

/**
 * Decides what the wrapper hands back for one chained call, and which state the
 * call's shape belongs to.
 *
 * `then` sends the request, and PostgREST re-sends on every `then`, so each
 * call is one round trip worth recording. Filter and transform methods mutate
 * and return the same builder, so their shape belongs to this wrapper's state.
 * `select()`, `insert()`, `update()`, and `delete()` return a NEW builder that
 * must stay wrapped or the rest of the chain escapes observation — and gets a
 * cloned state, because it is a separate request from the moment it exists.
 */
function resolveCallResult<T>(context: ObservationContext, call: ChainedCall<T>): unknown {
  const { args, name, proxy, result, startedAt, target } = call;
  if (name === SEND_METHOD) {
    recordSend(context, result, startedAt);
    return result;
  }
  if (result === target) {
    captureCall(context.state, name, args);
    return proxy;
  }
  if (isQueryBuilderLike(result)) {
    const branch = cloneQueryState(context.state);
    captureCall(branch, name, args);
    return observeQueryBuilder(result as QueryBuilder<T>, branch, context.observer, context.clock);
  }
  if (!maySendRequest(name)) {
    return result;
  }
  // A `single()`/`maybeSingle()` promise has already sent the request, so
  // record it here before the result bypasses the builder wrapper.
  if (isPromiseLike(result)) {
    recordSend(context, result, startedAt);
  }
  return result;
}

/**
 * Wraps a builder chain so its send is observed.
 *
 * The wrapper takes ownership of `state`: one state instance belongs to exactly
 * one builder, and a builder derived from this one gets a clone. Callers must
 * not hand the same instance to two wrappers.
 */
export function observeQueryBuilder<T>(
  builder: QueryBuilder<T>,
  state: QueryState,
  observer: QueryObserver,
  clock: () => number,
): QueryBuilder<T> {
  const context: ObservationContext = { clock, observer, state };

  const proxy: QueryBuilder<T> = new Proxy(builder as object, {
    get(target, property, receiver) {
      const value: unknown = Reflect.get(target, property, receiver);
      if (typeof value !== "function") {
        return value;
      }
      const name = String(property);

      return (...args: unknown[]) => {
        const startedAt = maySendRequest(name) ? clock() : 0;
        const result = Reflect.apply(value as (...a: unknown[]) => unknown, target, args);
        return resolveCallResult(context, {
          args,
          name,
          proxy,
          result,
          startedAt,
          target,
        });
      };
    },
  }) as QueryBuilder<T>;

  return proxy;
}
